/**
 * =============================================================================
 * Hook: useAffiliateMonthlyEvolution
 * Versão: 1.0.0
 *
 * Descrição:
 *   Carrega a evolução mensal dos últimos 6 meses do afiliado autenticado
 *   via RPC `get_affiliate_monthly_evolution` (server-side, SECURITY DEFINER).
 *
 *   O servidor resolve o afiliado pelo auth.uid(); o frontend NÃO envia IDs.
 *
 *   Retorna os dados já no formato consumido pelo componente reaproveitado
 *   `TaskTeamChart` (campo `month_label` + chaves de séries customizadas).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateMonthlyEvolution');

/** Linha do gráfico mensal. */
export interface AffiliateMonthlyEvolutionRow {
  /** Primeiro dia do mês (YYYY-MM-DD). */
  month_date: string;
  /** Rótulo abreviado pt-BR — ex.: "Jan/26". */
  month_label: string;
  /** Total de leads (referrals) criados no mês. */
  referrals_count: number;
  /** Total de conversões (status = converted) no mês. */
  conversions_count: number;
}

/**
 * Hook React Query — 6 meses de evolução do afiliado autenticado.
 */
export function useAffiliateMonthlyEvolution() {
  const { affiliate, ready } = useAffiliateAuth();

  return useQuery<AffiliateMonthlyEvolutionRow[]>({
    queryKey: ['affiliate-monthly-evolution', affiliate?.id],
    enabled: ready && !!affiliate?.id,
    staleTime: 60_000, // 1 minuto — dado histórico tolera leve atraso
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_affiliate_monthly_evolution' as never,
      );

      if (error) {
        log.error('Falha ao carregar evolução mensal do afiliado', error);
        throw error;
      }

      return (data as unknown as AffiliateMonthlyEvolutionRow[]) ?? [];
    },
  });
}
